import { useState, useRef, useEffect } from "react";
import { Tooltip } from "@heroui/react";
import { HiOutlineBell } from "react-icons/hi2";
import {
  useCancelNotification,
  useNotifications,
} from "../../../lib/react-query/notifications";
import {
  useApproveChatAccessRequest,
  useDenyChatAccessRequest,
} from "../../../lib/react-query/aiChat";
import { EmptyState } from "../EmptyState";

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const NotificationBell = ({ collapsed = false }) => {
  const [open, setOpen] = useState(false);
  const [cancellingNotificationId, setCancellingNotificationId] = useState(null);
  const dropdownRef = useRef(null);
  const { data, isLoading, isError } = useNotifications({ pageSize: 10 });
  const { mutateAsync: cancelNotification } = useCancelNotification();
  const { mutateAsync: approveRequest } = useApproveChatAccessRequest();
  const { mutateAsync: denyRequest } = useDenyChatAccessRequest();

  const notifications = data?.notifications || [];
  const totalCount = data?.total_count || 0;

  const handleCancelNotification = async (notificationId) => {
    setCancellingNotificationId(notificationId);
    try {
      await cancelNotification(notificationId);
    } catch (error) {
      console.error("Failed to cancel notification:", error);
    } finally {
      setCancellingNotificationId(null);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (collapsed) {
    return (
      <div className="w-full flex justify-center relative">
        <Tooltip content="Notifications" placement="right" delay={0} closeDelay={0}>
          <button
            onClick={() => setOpen(!open)}
            aria-label={
              totalCount > 0
                ? `Notifications, ${totalCount} total`
                : "Notifications"
            }
            aria-expanded={open}
            className="w-full flex items-center justify-center px-3 py-3 text-mediumgrey hover:bg-surface hover:text-darkgrey rounded-lg transition-all duration-200 relative"
          >
            <HiOutlineBell width={20} height={20} />
            {totalCount > 0 && (
              <>
                <span aria-hidden="true" className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-danger" />
                <span className="sr-only">{totalCount} total notifications</span>
              </>
            )}
          </button>
        </Tooltip>
        {open && (
          <div
            ref={dropdownRef}
            className="fixed left-16 top-1/2 -translate-y-1/2 z-popover w-80 bg-white rounded-xl shadow-xl border border-surface-border max-h-96 flex flex-col"
          >
            <NotificationDropdown
              notifications={notifications}
              isLoading={isLoading}
              isError={isError}
              totalCount={totalCount}
              onCancelNotification={handleCancelNotification}
              cancellingNotificationId={cancellingNotificationId}
              approveRequest={approveRequest}
              denyRequest={denyRequest}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <button
        onClick={() => setOpen(!open)}
        aria-label={
          totalCount > 0
            ? `Notifications, ${totalCount} total`
            : "Notifications"
        }
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-2 py-1.5 text-sm font-medium text-mediumgrey hover:bg-surface hover:text-grey rounded-lg transition-colors group relative"
      >
        <HiOutlineBell
          width={18}
          height={18}
          className="text-mediumgrey group-hover:text-darkgrey transition-colors"
        />
        <span>Notifications</span>
        {totalCount > 0 && (
          <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-danger rounded-full">
            {totalCount > 99 ? "99+" : totalCount}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={dropdownRef}
          className="absolute bottom-0 left-full ml-2 z-popover w-80 bg-white rounded-xl shadow-xl border border-surface-border max-h-96 flex flex-col"
        >
          <NotificationDropdown
            notifications={notifications}
            isLoading={isLoading}
            isError={isError}
            totalCount={totalCount}
            onCancelNotification={handleCancelNotification}
            cancellingNotificationId={cancellingNotificationId}
            approveRequest={approveRequest}
            denyRequest={denyRequest}
          />
        </div>
      )}
    </div>
  );
};

const NotificationDropdown = ({
  notifications,
  isLoading,
  isError,
  totalCount,
  onCancelNotification,
  cancellingNotificationId,
  approveRequest,
  denyRequest,
}) => {
  return (
    <>
      <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between flex-shrink-0">
        <h4 className="text-sm font-semibold text-primary">Notifications</h4>
        {totalCount > 0 && (
          <span className="text-xs text-mediumgrey">{totalCount} total</span>
        )}
      </div>
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-3 bg-surface rounded w-3/4 mb-2" />
                <div className="h-2.5 bg-surface rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="p-4">
            <p className="text-sm text-mediumgrey text-center">
              Could not load notifications. Try again later.
            </p>
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState
            title="No notifications yet"
            description="You'll see updates about your projects, teams, and invitations here."
            compact
          />
        ) : (
          <div className="divide-y divide-surface-border">
            {notifications.map((n) => (
              <div key={n.id} className="px-4 py-3 hover:bg-surface transition-colors">
                <p className="text-sm font-medium text-primary line-clamp-1">
                  {n.title || "Notification"}
                </p>
                {n.message && (
                  <p className="text-xs text-mediumgrey line-clamp-2 mt-0.5">
                    {n.message}
                  </p>
                )}
                <p className="text-[10px] text-grey mt-1">
                  {formatTimeAgo(n.sent_at || n.created_at)}
                </p>
                {(n.template_name === "chat_file_access_request" &&
                  n.action_context?.request_id) ||
                ["pending", "scheduled"].includes(n.status) ? (
                  <div className="mt-2 flex items-center gap-2">
                    {n.template_name === "chat_file_access_request" &&
                    n.action_context?.request_id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => approveRequest(n.action_context.request_id)}
                          className="rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-white"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => denyRequest({ requestId: n.action_context.request_id })}
                          className="rounded-md border border-primary/15 px-2 py-1 text-[11px] font-medium text-primary"
                        >
                          Deny
                        </button>
                      </>
                    ) : null}
                    {["pending", "scheduled"].includes(n.status) ? (
                      <button
                        type="button"
                        onClick={() => onCancelNotification(n.id)}
                        disabled={cancellingNotificationId === n.id}
                        aria-label={`Cancel ${n.title || "notification"}`}
                        className="rounded-md border border-danger/20 px-2 py-1 text-[11px] font-medium text-danger disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {cancellingNotificationId === n.id ? "Cancelling…" : "Cancel"}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
